import { DOCUMENT } from "@angular/common";
import { Inject, Injectable, LOCALE_ID, Renderer2, RendererFactory2 } from "@angular/core";
import { Meta, Title } from "@angular/platform-browser";
import { Client, Job, Page } from "@interfaces";
import { environment } from "src/environments/environment";
import { SubdomainService } from "../rest/subdomain.service";

@Injectable({
    providedIn: "root",
})
export class MetadataService {
    private renderer: Renderer2;
    constructor(
        private title: Title,
        private meta: Meta,
        private subdomain: SubdomainService,
        private rendererFactory: RendererFactory2,
        @Inject(DOCUMENT) private document: Document,
        @Inject(LOCALE_ID) private locale: string
    ) {
        this.renderer = this.rendererFactory.createRenderer(null, null);
    }

    public setupJobMetaTags(job: Job) {
        this.subdomain.getClient().subscribe((subdomain: Client) => {
            this.title.setTitle(`${subdomain?.name || "Voyse"} - ${job?.title}`);
			this.updateCanonicalUrl(job.permalink);
            this.meta.addTags([
                {
                    name: "description",
                    property: "description",
                    content:
                        job?.metaDescription ||
                        subdomain?.metaText ||
                        `Learn more about working for ${subdomain.name} here. Powered by Voyse.`,
                },
                { name: "title", property: "og:title", content: `${subdomain?.displayName || subdomain.name} - ${job?.title}` },
                { property: "og:type", content: "article" },
                {
                    property: "og:description",
                    content:
                        job?.metaDescription ||
                        subdomain?.metaText ||
                        `Learn more about working for ${subdomain?.name} here. Powered by Voyse.`,
                },
                { property: "og:url", content: job.permalink },
                { property: "article:published_time", content: job?.createdAt.toString() },
                { property: "article:modified_time", content: job?.updatedAt.toString() },
            ]);

            if (subdomain.integrations?.searchconsole?.apiKey) {
                this.meta.addTags([
                    { name: "google-site-verification", content: subdomain.integrations.searchconsole.apiKey },
                ]);
            }

            if (job?.metaImage?.public_id) {
                this.meta.addTags([
                    {
                        type: "image",
                        property: "og:image",
                        content: `https://res.cloudinary.com/minirobot/image/upload/c_fit,h_260,w_500/v1/${
                            job.metaImage.public_id
                        }.${job.metaImage.format || "jpg"}`,
                    },
                    {
                        property: "og:image:secure_url",
                        content: `https://res.cloudinary.com/minirobot/image/upload/c_fit,h_260,w_500/v1/${
                            job.metaImage.public_id
                        }.${job.metaImage.format || "jpg"}`,
                    },
                    { property: "og:image:width", content: "500" },
                    { property: "og:image:height", content: "260" },
                ]);
            } else if (subdomain?.jobsMetaImage?.public_id) {
                this.meta.addTags([
                    {
                        type: "image",
                        property: "og:image",
                        content: `https://res.cloudinary.com/minirobot/image/upload/c_fit,h_600,w_1200/v1/${
                            subdomain.jobsMetaImage.public_id
                        }.${subdomain.jobsMetaImage?.format || "jpg"}`,
                    },
                    {
                        property: "og:image:secure_url",
                        content: `https://res.cloudinary.com/minirobot/image/upload/c_fit,h_600,w_1200/v1/${
                            subdomain.jobsMetaImage.public_id
                        }.${subdomain.jobsMetaImage?.format || "jpg"}`,
                    },
                    { property: "og:image:width", content: "1200" },
                    { property: "og:image:height", content: "600" },
                ]);
            } else if (job.video && job.videoData) {
                const thumbnail = job.videoData?.customThumbnail?.public_id
                    ? `https://res.cloudinary.com/minirobot/image/upload/c_fit,h_260,w_500/v1/${
                          job.videoData?.customThumbnail?.public_id
                      }.${job.videoData?.customThumbnail?.format || "jpg"}`
                    : job.videoData?.thumbnail;
                this.meta.addTags([
                    { type: "image", property: "og:image", content: thumbnail },
                    { property: "og:image:secure_url", content: thumbnail },
                    { property: "og:image:width", content: "500" },
                    { property: "og:image:height", content: "260" },
                ]);
            } else {
                this.meta.addTags([
                    {
                        type: "image",
                        property: "og:image",
                        content: `https://res.cloudinary.com/minirobot/image/upload/c_fit,h_260,w_500/v1/voyse/assets/meta-image.jpg`,
                    },
                    {
                        property: "og:image:secure_url",
                        content: `https://res.cloudinary.com/minirobot/image/upload/c_fit,h_260,w_500/v1/voyse/assets/meta-image.jpg`,
                    },
                    { property: "og:image:width", content: "500" },
                    { property: "og:image:height", content: "260" },
                ]);
            }
        });
    }

    updateCanonicalUrl(url: string) {
        const head = this.document.getElementsByTagName("head")[0];
        var element: HTMLLinkElement = this.document.querySelector(`link[rel='canonical']`) || null;
        if (element == null) {
            element = this.renderer.createElement("link") as HTMLLinkElement;
            this.renderer.appendChild(head, element);
        }
        this.renderer.setAttribute(element, "rel", "canonical");
        this.renderer.setAttribute(element, "href", url);
    }

    public preloadImage(url: string) {
        const head = this.document.getElementsByTagName("head")[0];
        var element: HTMLLinkElement = this.document.querySelector(`link[rel='preload']`) || null;
        if (element == null) {
            element = this.renderer.createElement("link") as HTMLLinkElement;
            this.renderer.appendChild(head, element);
        }
        this.renderer.setAttribute(element, "as", "image");
        this.renderer.setAttribute(element, "rel", "preload");
        this.renderer.setAttribute(element, "href", url);
    }

    public setupPageMetaTags(page: Page) {
        this.subdomain.getClient().subscribe((subdomain: Client) => {
            this.title.setTitle(`${subdomain?.name} ${page.title}`);
			this.updateCanonicalUrl(page.permalink);
            this.meta.addTags([
                {
                    name: "title",
                    property: "og:title",
                    content: `${subdomain?.displayName || subdomain.name} - ${page?.additionalTitles?.[this.locale] || page?.title}`,
                },
                {
                    name: "description",
                    property: "description",
                    content:
                        page?.metaDescription ||
                        subdomain?.metaText ||
                        `Learn more about working for ${subdomain.name} here. Powered by Voyse.`,
                },
                { property: "og:type", content: "website" },
                {
                    property: "og:description",
                    content:
                        page?.additionalMetadata?.[this.locale]?.metaDescription ||
                        page.metaDescription ||
                        subdomain?.metaText ||
                        `Learn more about working for ${subdomain?.name} here. Powered by Voyse.`,
                },
                { property: "og:url", content: page.permalink },
                { property: "article:published_time", content: page.createdAt.toString() },
                { property: "article:modified_time", content: page.updatedAt.toString() },
            ]);

            if (subdomain.integrations?.searchconsole?.apiKey) {
                this.meta.addTags([
                    { name: "google-site-verification", content: subdomain.integrations.searchconsole.apiKey },
                ]);
            }

            page.languages?.forEach((language: string) => {
                this.addHrefLangTags(page, language, subdomain);
            });

            if (page.metaImage?.public_id) {
                this.meta.addTags([
                    {
                        type: "image",
                        property: "og:image",
                        content: `https://res.cloudinary.com/minirobot/image/upload/c_fit,h_260,w_500/v1/${
                            page.metaImage.public_id
                        }.${page.metaImage?.format || "jpg"}`,
                    },
                    {
                        property: "og:image:secure_url",
                        content: `https://res.cloudinary.com/minirobot/image/upload/c_fit,h_260,w_500/v1/${
                            page.metaImage.public_id
                        }.${page.metaImage?.format || "jpg"}`,
                    },
                    { property: "og:image:width", content: "500" },
                    { property: "og:image:height", content: "260" },
                ]);
            } else if (subdomain?.jobsMetaImage?.public_id) {
                this.meta.addTags([
                    {
                        type: "image",
                        property: "og:image",
                        content: `https://res.cloudinary.com/minirobot/image/upload/c_fit,h_600,w_1200/v1/${
                            subdomain.jobsMetaImage.public_id
                        }.${subdomain.jobsMetaImage?.format || "jpg"}`,
                    },
                    {
                        property: "og:image:secure_url",
                        content: `https://res.cloudinary.com/minirobot/image/upload/c_fit,h_600,w_1200/v1/${
                            subdomain.jobsMetaImage.public_id
                        }.${subdomain.jobsMetaImage?.format || "jpg"}`,
                    },
                    { property: "og:image:width", content: "1200" },
                    { property: "og:image:height", content: "600" },
                ]);
            } else {
                this.meta.addTags([
                    {
                        type: "image",
                        property: "og:image",
                        content: `https://res.cloudinary.com/minirobot/image/upload/c_fit,h_260,w_500/v1/voyse/assets/meta-image.jpg`,
                    },
                    {
                        property: "og:image:secure_url",
                        content: `https://res.cloudinary.com/minirobot/image/upload/c_fit,h_260,w_500/v1/voyse/assets/meta-image.jpg`,
                    },
                    { property: "og:image:width", content: "500" },
                    { property: "og:image:height", content: "260" },
                ]);
            }
        });
    }

    private addHrefLangTags(page: Page, lang: string, client: Client) {
        if (lang) {
            const linkElt = this.renderer.createElement("link");
            this.renderer.setAttribute(linkElt, "rel", "alternate");
            this.renderer.setAttribute(linkElt, "hreflang", lang);
            // Split page.permalink and append the language code after the first slash
            if (client.alias) {
                const url = `https://${client.alias}/${lang}/${page.defaultPage ? "" : page.slug}`;
                this.renderer.setAttribute(linkElt, "href", url);
                this.renderer.appendChild(this.document.head, linkElt);
            } else {
                const url = `${environment.domain.replace("foo", client.slug)}/${lang}/${
                    page.defaultPage ? "" : `${page.slug}`
                }`;
                this.renderer.setAttribute(linkElt, "href", url);
                this.renderer.appendChild(this.document.head, linkElt);
            }
        }
    }

    public tearDownMetaTags() {
        this.meta.removeTag("property='og:title'");
        this.meta.removeTag("property='og:type'");
        this.meta.removeTag("property='og:site_name'");
        this.meta.removeTag("property='og:url'");
        this.meta.removeTag("property='og:video:url'");
        this.meta.removeTag("property='og:video:secure_url'");
        this.meta.removeTag("property='og:video:type'");
        this.meta.removeTag("property='og:description'");
        this.meta.removeTag("property='article:published_time'");
        this.meta.removeTag("property='article:modified_time'");
        this.meta.removeTag("property='og:image'");
        this.meta.removeTag("property='og:image:secure_url'");
        this.meta.removeTag("property='og:image:width'");
        this.meta.removeTag("property='og:image:height'");
        this.meta.removeTag("name='google-site-verification'");
    }
}
